import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { compose } from "redux";
import { FiExternalLink, FiMail, FiPhone } from "react-icons/fi";
import { FaMagic } from "react-icons/fa";
import { TbPencil } from "react-icons/tb";

import Drawer from "components/Drawer";
import Avatar from "components/Avatar";
import AvatarEdit from "components/Avatar/AvatarEdit";
import Loading from "components/Loading";
import EmployeeInfo from "components/Employees/EmployeeInfo";
import EmployeeCustomFields from "components/Employees/EmployeeCustomFields";
import EmployeeGroups from "components/Employees/EmployeeGroups";
import EmployeeFunctions from "components/Employees/EmployeeFunctions";
import EmployeeContracts from "components/Employees/EmployeeContracts";
import EmployeeDocuments from "components/Employees/EmployeeDocuments";
import EmployeeUbications from "components/Employees/EmployeeUbications";
import EmployeeShiftPatterns from "components/Employees/EmployeeShiftPatterns";
import EmployeeMagicPlan from "components/Employees/EmployeeMagicPlan";
import EmployeePayroll from "components/Employees/EmployeePayroll";
import EmployeePendingConfigurationWarnings from "components/Employees/EmployeePendingConfigurationWarnings";

import { employeesService, dashboardsService } from "services";
import { showModal, hideModal } from "actions/modalActions";
import * as employeesActions from "actions/employeesActions";
import * as groupsActions from "actions/groupsActions";
import * as shiftsActions from "actions/shiftsActions";
import * as functionsActions from "actions/functionsActions";
import * as ubicationsActions from "actions/ubicationsActions";
import * as rulesActions from "actions/rulesActions";
import * as customFieldsActions from "actions/customFieldsActions";
import * as dashboardActions from "actions/dashboardActions";
import { isAdmin, isSupervisor } from "utils/utils";
import { withTranslation } from "react-i18next";

import "./EmployeeEditDrawer.scss";

const TAB_DEFINITIONS = [
	{ eventKey: "info", labelKey: "employeeDrawer.tabs.info" },
	{ eventKey: "contratos", labelKey: "employeeDrawer.tabs.contracts" },
	{ eventKey: "grupos", labelKey: "employeeDrawer.tabs.groups" },
	{ eventKey: "ruedas-turnos", labelKey: "employeeDrawer.tabs.shiftPatterns" },
	{ eventKey: "funciones", labelKey: "employeeDrawer.tabs.functions" },
	{ eventKey: "ubicaciones", labelKey: "employeeDrawer.tabs.ubications" },
	{ eventKey: "documentos", labelKey: "employeeDrawer.tabs.documents", adminOnly: true },
	{ eventKey: "campos-personalizados", labelKey: "employeeDrawer.tabs.customFields" },
	{ eventKey: "nomina", labelKey: "employeeDrawer.tabs.payroll", requiresAdmin: true },
	{
		eventKey: "planificacion",
		labelKey: "employeeDrawer.tabs.aiPlanning",
		icon: <FaMagic className="icon-wand" />,
	},
];

/**
 * Contenido interno del drawer (lógica + UI) sin el wrapper <Drawer/>.
 * Se usa tanto desde el overlay "montable" como desde el helper openEmployeeDrawer
 * (que lo inyecta dentro de un DrawerModal a través de showModal).
 */
class EmployeeEditDrawerContent extends React.Component {
	constructor(props) {
		super(props);

		const initialEmployee =
			props.employee && props.employee.id ? props.employee : null;

		this.state = {
			loading: !initialEmployee,
			employee: initialEmployee || {},
			vacationData: null,
			hourData: null,
			activeTab: props.initialTab || "info",
			loadedTabs: new Set(),
			documentosRefreshKey: 0,
			configurationWarningsRefreshKey: 0,
			assignmentModalTab: props.initialAssignmentTab || null,
		};
	}

	async componentDidMount() {
		await this.loadEmployee();
	}

	async componentDidUpdate(prevProps) {
		const prevId = prevProps.employee_id || (prevProps.employee && prevProps.employee.id);
		const currId = this.props.employee_id || (this.props.employee && this.props.employee.id);
		const employeeChanged = Boolean(currId && prevId !== currId);
		const assignmentRequestChanged =
			prevProps.openRequestId !== this.props.openRequestId ||
			prevProps.initialTab !== this.props.initialTab ||
			prevProps.initialAssignmentTab !== this.props.initialAssignmentTab;

		if (!employeeChanged && !assignmentRequestChanged) return;

		const nextTab = this.props.initialTab || "info";
		this.setState(
			{
				loading: employeeChanged ? true : this.state.loading,
				employee: employeeChanged ? this.props.employee || {} : this.state.employee,
				loadedTabs: employeeChanged ? new Set() : this.state.loadedTabs,
				activeTab: nextTab,
				assignmentModalTab: this.props.initialAssignmentTab || null,
			},
			() => {
				if (!this.props.initialAssignmentTab && typeof this.props.hideModal === "function") {
					this.props.hideModal();
				}
				if (employeeChanged) {
					this.loadEmployee();
					return;
				}
				this.loadTabData(nextTab);
			}
		);
	}

	componentWillUnmount() {
		if (typeof this.props.clearEmployeeState === "function") {
			this.props.clearEmployeeState();
		}
	}

	loadEmployee = async () => {
		const { employee_id, employee } = this.props;

		const idToLoad = employee_id || (employee && employee.id);
		if (!idToLoad) {
			this.setState({ loading: false });
			return;
		}

		const response = await employeesService.getById(idToLoad);
		const currentId = this.props.employee_id || (this.props.employee && this.props.employee.id);
		if (String(currentId) !== String(idToLoad)) return;

		let employeeData = response && response.ok ? response : employee;

		if (!employeeData || !employeeData.id) {
			this.setState({ loading: false });
			return;
		}

		this.setState(
			{
				employee: employeeData,
				loading: false,
				loadedTabs: new Set(),
				// Conservar la asignación pedida al abrir (grupo, contrato o rueda).
				// Si se anula aquí, la pestaña se pinta pero el modal de creación no llega a abrirse.
				assignmentModalTab: this.props.initialAssignmentTab || null,
			},
			() => {
				if (typeof this.props.updateEmployeeInState === "function") {
					this.props.updateEmployeeInState(employeeData);
				}
				this.loadSidebarData(employeeData);
				this.loadTabData(this.state.activeTab || this.props.initialTab || "info");
			}
		);
	};

	loadSidebarData = async (employee) => {
		if (!employee || !employee.id) return;

		await Promise.all([
			this.loadVacationData(employee),
			this.loadHourData(employee),
			typeof this.props.getWidgetTemplates === "function"
				? this.props.getWidgetTemplates()
				: Promise.resolve(),
		]);

		if (!this.state.loadedTabs.has(this.state.activeTab)) {
			await this.loadTabData(this.state.activeTab);
		}
	};

	loadVacationData = async (employee) => {
		try {
			if (!employee || !employee.id) return;
			const currentYear = new Date().getFullYear();
			const response = await dashboardsService.getEmployeeVacationSummary(
				employee.id,
				currentYear
			);
			if (response.ok) this.setState({ vacationData: response });
		} catch (_) {
			// info complementaria del sidebar
		}
	};

	loadHourData = async (employee) => {
		try {
			if (!employee || !employee.id) return;
			const response = await dashboardsService.getEmployeeHourSummary(
				employee.id
			);
			if (response.ok) this.setState({ hourData: response });
		} catch (_) {
			// info complementaria del sidebar
		}
	};

	loadTabData = async (tabKey) => {
		const loadingPromises = [];

		switch (tabKey) {
			case "grupos":
				if (typeof this.props.getAllGroups === "function") {
					loadingPromises.push(this.props.getAllGroups());
				}
				break;
			case "campos-personalizados":
				if (typeof this.props.getAllCustomFields === "function") {
					loadingPromises.push(this.props.getAllCustomFields());
				}
				break;
			case "funciones":
				if (typeof this.props.getEnabledFunctions === "function") {
					loadingPromises.push(this.props.getEnabledFunctions());
				}
				break;
			case "ruedas-turnos":
				if (typeof this.props.getSelectableShiftPatterns === "function") {
					loadingPromises.push(this.props.getSelectableShiftPatterns());
				}
				break;
			case "ubicaciones":
				if (typeof this.props.getEnabledUbications === "function") {
					loadingPromises.push(this.props.getEnabledUbications());
				}
				break;
			case "contratos":
				if (typeof this.props.getContracts === "function") {
					loadingPromises.push(this.props.getContracts());
				}
				break;
			case "documentos":
				this.setState((prev) => ({
					documentosRefreshKey: (prev.documentosRefreshKey || 0) + 1,
				}));
				break;
			default:
				break;
		}

		if (loadingPromises.length > 0) {
			await Promise.all(loadingPromises);
		}

		this.setState((prev) => ({
			loadedTabs: new Set([...prev.loadedTabs, tabKey]),
		}));
	};

	handleTabSelect = async (tabKey) => {
		if (!tabKey) return;
		this.setState({ activeTab: tabKey });
		await this.loadTabData(tabKey);
	};

	handleConfigurationChanged = () => {
		this.setState((prevState) => ({
			configurationWarningsRefreshKey:
				prevState.configurationWarningsRefreshKey + 1,
		}));

		if (typeof this.props.onDataChanged === "function") {
			this.props.onDataChanged();
		}
	};

	handleConfigurationWarningAction = async (tabKey) => {
		await this.handleTabSelect(tabKey);
		this.setState({ assignmentModalTab: tabKey });
	};

	handleAssignmentRequestHandled = () => {
		this.setState({ assignmentModalTab: null });
	};

	syncEmployeeFromTab = (updatedEmployee) => {
		if (!updatedEmployee || !updatedEmployee.id) return;

		this.setState({ employee: updatedEmployee });
		if (typeof this.props.updateEmployeeInState === "function") {
			this.props.updateEmployeeInState(updatedEmployee);
		}
	};

	handleEmployeeUpdated = (updatedEmployee) => {
		if (!updatedEmployee || !updatedEmployee.id) return;

		this.setState({ employee: updatedEmployee });
		if (typeof this.props.updateEmployeeInState === "function") {
			this.props.updateEmployeeInState(updatedEmployee);
		}
		if (typeof this.props.onDataChanged === "function") {
			this.props.onDataChanged();
		}
		this.loadSidebarData(updatedEmployee);
	};

	openAvatarModal = () => {
		const { employee } = this.state;
		if (!employee || !employee.id) return;

		this.props.showModal(
			{
				open: true,
				title: this.props.t("employeeDrawer.editAvatar"),
				style: { width: "300px" },
				content: (
					<AvatarEdit
						userId={employee.id}
						onClose={() => this.props.hideModal()}
						onDataChanged={this.props.onDataChanged}
					/>
				),
				closeModal: () => this.props.hideModal(),
			},
			"empty"
		);
	};

	goToFullProfile = () => {
		const { employee } = this.state;
		if (!employee || !employee.id) return;

		if (typeof this.props.handleClose === "function") {
			this.props.handleClose();
		}
		if (this.props.history) {
			this.props.history.push(`/employees/${employee.id}`);
		}
	};

	formatSeniority = (seniority) => {
		const { t } = this.props;
		if (!seniority) return t("employeeDrawer.seniority.noSeniority");

		const { years = 0, months = 0, days = 0 } = seniority;
		const parts = [];
		if (years > 0) parts.push(t("employeeDrawer.seniority.years", { count: years }));
		if (months > 0) parts.push(t("employeeDrawer.seniority.months", { count: months }));
		if (days > 0) parts.push(t("employeeDrawer.seniority.days", { count: days }));

		if (parts.length === 0) return t("employeeDrawer.seniority.noSeniority");
		if (parts.length === 1) return parts[0];
		if (parts.length === 2) return `${parts[0]} ${t("employeeDrawer.seniority.and")} ${parts[1]}`;
		return `${parts[0]}, ${parts[1]} ${t("employeeDrawer.seniority.and")} ${parts[2]}`;
	};

	getAvailableTabs = () => {
		const groupsSupervised =
			(this.props.user && this.props.user.groups_supervised) || [];
		const isSupervisorWithEditDocuments =
			isSupervisor() &&
			groupsSupervised.length > 0 &&
			groupsSupervised.some((gs) => gs.edit_documents);
		const showAdminDocuments = isAdmin() || isSupervisorWithEditDocuments;

		return TAB_DEFINITIONS.filter((tab) => {
			if (tab.requiresAdmin) return isAdmin();
			if (tab.adminOnly) return showAdminDocuments;
			return true;
		});
	};

	renderSidebarHeader = () => {
		const { employee, vacationData, hourData } = this.state;
		const workedHours =
			(hourData &&
				hourData.current_week &&
				hourData.current_week.time_registration_hours) ||
			0;
		const targetHours =
			(hourData &&
				hourData.current_week &&
				hourData.current_week.target_hours) ||
			0;
		const vacationPending =
			(vacationData && vacationData.vacation_pending) || 0;

		return (
			<div className="employee-drawer-profile">
				<div className="employee-drawer-profile__avatar">
					<Avatar
						onClick={this.openAvatarModal}
						userId={employee.id}
						alt={employee.name}
					/>
					<button
						type="button"
						className="employee-drawer-profile__avatar-edit"
						aria-label={this.props.t("employeeDrawer.editAvatar")}
						onClick={this.openAvatarModal}
					>
						<TbPencil />
					</button>
				</div>

				<div className="employee-drawer-profile__info">
					<h5 className="employee-drawer-profile__name">
						{employee.name} {employee.surname}
					</h5>
					{employee.position && (
						<p className="employee-drawer-profile__position">
							{employee.position}
						</p>
					)}
					<p className="employee-drawer-profile__seniority">
						{this.formatSeniority(employee.seniority)}
					</p>

					{(employee.email || employee.phone) && (
						<div className="employee-drawer-profile__contact">
							{employee.email && (
								<a
									className="employee-drawer-profile__contact-item"
									href={`mailto:${employee.email}`}
									title={employee.email}
								>
									<FiMail size={12} />
									<span>{employee.email}</span>
								</a>
							)}
							{employee.phone && (
								<a
									className="employee-drawer-profile__contact-item"
									href={`tel:${employee.phone}`}
									title={employee.phone}
								>
									<FiPhone size={12} />
									<span>{employee.phone}</span>
								</a>
							)}
						</div>
					)}

					{/* <div className="employee-drawer-profile__stats">
						<div className="employee-drawer-profile__stat">
							<span className="employee-drawer-profile__stat-label">
								Esta semana
							</span>
							<span className="employee-drawer-profile__stat-value">
								{workedHours.toLocaleString(undefined, {
									maximumFractionDigits: 2,
								})}
								{" / "}
								{targetHours.toLocaleString(undefined, {
									maximumFractionDigits: 2,
								})}{" "}
								h
							</span>
						</div>
						<div className="employee-drawer-profile__stat">
							<span className="employee-drawer-profile__stat-label">
								Vacaciones disponibles
							</span>
							<span className="employee-drawer-profile__stat-value">
								{vacationPending.toLocaleString(undefined, {
									maximumFractionDigits: 2,
								})}
							</span>
						</div>
					</div> */}
				</div>
			</div>
		);
	};

	renderTabContent = (tabKey, employee) => {
		const { loadedTabs, activeTab, documentosRefreshKey } = this.state;
		const { onDataChanged } = this.props;
		if (!loadedTabs.has(tabKey)) return null;

		switch (tabKey) {
			case "info":
				return (
					<EmployeeInfo
						employee={employee}
						onEmployeeUpdated={this.handleEmployeeUpdated}
					/>
				);
			case "campos-personalizados":
				return <EmployeeCustomFields employee={employee} />;
			case "grupos":
				return (
					<EmployeeGroups
						employee={employee}
						onDataChanged={this.handleConfigurationChanged}
						openAssignment={this.state.assignmentModalTab === "grupos"}
						onAssignmentRequestHandled={this.handleAssignmentRequestHandled}
					/>
				);
			case "funciones":
				return (
					<EmployeeFunctions employee={employee} onDataChanged={onDataChanged} />
				);
			case "ruedas-turnos":
				return (
					<EmployeeShiftPatterns
						employee={employee}
						onDataChanged={this.handleConfigurationChanged}
						openAssignment={this.state.assignmentModalTab === "ruedas-turnos"}
						onAssignmentRequestHandled={this.handleAssignmentRequestHandled}
					/>
				);
			case "ubicaciones":
				return (
					<EmployeeUbications employee={employee} onDataChanged={onDataChanged} />
				);
			case "contratos":
				return (
					<EmployeeContracts
						employee={employee}
						onDataChanged={this.handleConfigurationChanged}
						openAssignment={this.state.assignmentModalTab === "contratos"}
						onAssignmentRequestHandled={this.handleAssignmentRequestHandled}
					/>
				);
			case "documentos":
				return (
					<EmployeeDocuments
						employee={employee}
						isVisible={activeTab === "documentos"}
						refreshKey={documentosRefreshKey}
					/>
				);
			case "planificacion":
				return (
					<EmployeeMagicPlan
						employee={employee}
						onDataChanged={onDataChanged}
						onEmployeeUpdated={this.syncEmployeeFromTab}
					/>
				);
			case "nomina":
				return (
					<EmployeePayroll
						employee={employee}
						onEmployeeUpdated={this.handleEmployeeUpdated}
					/>
				);
			default:
				return null;
		}
	};

	render() {
		const { loading, employee, activeTab } = this.state;
		const { t } = this.props;

		if (loading) {
			return (
				<Drawer.Main>
					<Drawer.MainHeader title={t("employeeDrawer.loading")} />
					<Drawer.Body>
						<Loading />
					</Drawer.Body>
				</Drawer.Main>
			);
		}

		if (!employee || !employee.id) {
			return (
				<Drawer.Main>
					<Drawer.MainHeader title={t("employeeDrawer.unavailable")} />
					<Drawer.Body>
						<p>{t("employeeDrawer.loadError")}</p>
					</Drawer.Body>
					<Drawer.Footer>
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary"
						>
							{t("common.close")}
						</button>
					</Drawer.Footer>
				</Drawer.Main>
			);
		}

		const tabs = this.getAvailableTabs();
		const currentTab = tabs.find((tab) => tab.eventKey === activeTab) || tabs[0];

		return (
			<Drawer.Tabs activeKey={activeTab} onSelect={this.handleTabSelect}>
				<Drawer.Sidebar className="employee-drawer-sidebar" width={300}>
					<Drawer.SidebarHeader>
						{this.renderSidebarHeader()}
					</Drawer.SidebarHeader>
					<Drawer.Nav>
						{tabs.map((tab) => (
							<Drawer.NavItem
								key={tab.eventKey}
								eventKey={tab.eventKey}
								icon={tab.icon}
							>
								{t(tab.labelKey)}
							</Drawer.NavItem>
						))}
					</Drawer.Nav>
				</Drawer.Sidebar>

				<Drawer.Main>
					<Drawer.MainHeader
						title={currentTab ? t(currentTab.labelKey) : ""}
						actions={
							<button
								type="button"
								className="btn-transparent employee-drawer-full-profile"
								onClick={this.goToFullProfile}
							>
								{t("employeeDrawer.viewFullProfile")} <FiExternalLink size={12} />
							</button>
						}
					/>

					<EmployeePendingConfigurationWarnings
						employeeId={employee.id}
						onNavigate={this.handleConfigurationWarningAction}
						refreshKey={this.state.configurationWarningsRefreshKey}
						className="mx-4 mb-1"
					/>

					<Drawer.Body className="employee-drawer-body">
						{tabs.map((t) => (
							<Drawer.TabPane
								key={t.eventKey}
								eventKey={t.eventKey}
								mountOnEnter
								unmountOnExit
							>
								{this.renderTabContent(t.eventKey, employee)}
							</Drawer.TabPane>
						))}
					</Drawer.Body>

					<Drawer.Footer>
						<button
							type="button"
							onClick={this.props.handleClose}
							className="btn btn-outline-primary"
						>
							{t("common.close")}
						</button>
					</Drawer.Footer>
				</Drawer.Main>
			</Drawer.Tabs>
		);
	}
}

const mapStateToProps = (reducers) => {
	// `employeesReducer.employee` es el último empleado cargado en el store (p. ej. el admin
	// que editó su ficha). Si lo mezclamos en el spread, react-redux pisa la prop `employee`
	// que pasa el padre (fila de la lista / drawer) y `loadEmployee` + componentDidUpdate
	// usan datos del usuario equivocado → heurística "admin principal" para todos los EMPLOYEE
	// creados cerca del alta de la compañía.
	const employeesState = reducers.employeesReducer || {};
	const { employee: _singletonEmployeeFromStore, ...employeesRest } =
		employeesState;

	return {
		...employeesRest,
		...reducers.groupsReducer,
		...reducers.functionsReducer,
		...reducers.ubicationsReducer,
		...reducers.shiftsReducer,
		...reducers.rulesReducer,
		...reducers.customFieldsReducer,
		...reducers.dashboardReducer,
		user: reducers.authReducer.user,
	};
};

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
	getAllCustomFields: () => dispatch(customFieldsActions.getAllCustomFields()),
	getAllGroups: () =>
		dispatch(groupsActions.getAllGroups({ is_multigroup: false })),
	getEnabledFunctions: () => dispatch(functionsActions.getEnabledFunctions()),
	getEnabledUbications: () =>
		dispatch(ubicationsActions.getEnabledUbications()),
	getContracts: () => dispatch(rulesActions.getContracts()),
	getSelectableShiftPatterns: () =>
		dispatch(shiftsActions.getSelectableShiftPatterns()),
	getWidgetTemplates: () => dispatch(dashboardActions.getWidgetTemplates()),
	updateEmployeeInState: (employee) =>
		dispatch(employeesActions.updateEmployeeInState(employee)),
	clearEmployeeState: () => dispatch(employeesActions.clearEmployeeState()),
});

const ConnectedEmployeeEditDrawerContent = compose(
	withRouter,
	connect(mapStateToProps, mapDispatchToProps),
	withTranslation()
)(EmployeeEditDrawerContent);

/**
 * Componente "overlay" — monta su propio <Drawer/> y lo controla vía props
 * `isOpen` y `onClose`. Gracias a createPortal + backdrop propios, puede
 * apilarse sobre otros drawers (ej: abrir el drawer de empleado desde dentro
 * del drawer de contrato).
 *
 * Props:
 *  - isOpen: boolean (requerido)
 *  - onClose: (hasDataChanges?: boolean) => void (requerido)
 *  - employee?: object — empleado (si ya lo tenemos, evita una petición inicial)
 *  - employeeId?: number | string — id para cargar el empleado
 *  - initialTab?: string — pestaña inicial (default: "info")
 *  - width?: number | string (default: 1100)
 */
const EmployeeEditDrawer = ({
	isOpen,
	onClose,
	onDataChanged,
	employee,
	employeeId,
	initialTab,
	initialAssignmentTab,
	openRequestId,
	width = 1100,
}) => {
	const hasDataChangesRef = React.useRef(false);
	const drawerEmployeeKey = employeeId || (employee && employee.id);

	React.useEffect(() => {
		if (isOpen) {
			hasDataChangesRef.current = false;
		}
	}, [isOpen, drawerEmployeeKey]);

	const markDataChanged = React.useCallback(() => {
		hasDataChangesRef.current = true;
		if (typeof onDataChanged === "function") {
			onDataChanged();
		}
	}, [onDataChanged]);

	const handleClose = React.useCallback(() => {
		if (typeof onClose === "function") {
			onClose(hasDataChangesRef.current);
		}
	}, [onClose]);

	if (!isOpen) return null;

	return (
		<Drawer isOpen={isOpen} onClose={handleClose} width={width}>
				<ConnectedEmployeeEditDrawerContent
					employee={employee}
					employee_id={drawerEmployeeKey}
					initialTab={initialTab}
					initialAssignmentTab={initialAssignmentTab}
					openRequestId={openRequestId}
					handleClose={handleClose}
					onDataChanged={markDataChanged}
				/>
		</Drawer>
	);
};

export default EmployeeEditDrawer;

/**
 * Helper: abrir el drawer vía el sistema Redux de modales (showModal / "drawer").
 * Útil cuando no se quiere mantener estado local en el componente padre.
 *
 * ⚠️ Solo para usos top-level: el modalReducer solo guarda un modal a la vez,
 * así que desde dentro de otro drawer NO debe usarse este helper (reemplazaría
 * al drawer padre). Para apilado usa <EmployeeEditDrawer isOpen ... />.
 */
export const openEmployeeDrawer = ({
	showModal: showModalFn,
	employee,
	employeeId,
	initialTab,
	width = 1100,
	onClose,
}) => {
	if (typeof showModalFn !== "function") return;

	showModalFn(
		{
			open: true,
			width,
			content: ({ close, registerBeforeClose }) => (
				<ConnectedEmployeeEditDrawerContent
					employee={employee}
					employee_id={employeeId || (employee && employee.id)}
					initialTab={initialTab}
					handleClose={() => {
						close();
						if (typeof onClose === "function") {
							onClose();
						}
					}}
					registerBeforeClose={registerBeforeClose}
				/>
			),
		},
		"drawer"
	);
};

const EMPLOYEE_LINK_ATTR = "data-employee-link";

/**
 * React 16 delega onClick en document (burbuja). En el browser integrado de
 * Cursor/VS Code y en algunos Chromium el <a href> navega igual porque el
 * default ocurre antes de que el preventDefault sintético llegue. Un listener
 * nativo en window (captura) sí llega a tiempo: el drawer se abre y la ruta
 * no cambia.
 *
 * Cmd/Ctrl/Alt/Shift se dejan pasar para abrir la ficha en pestaña nueva.
 * No usamos `e.button === 1` como atajo a "middle click": en el modelo IE
 * (algunos Chrome de Windows) el click izquierdo también es button === 1, y
 * eso saltaba el preventDefault y restauraba la navegación antigua.
 */
function isModifiedClick(event) {
	return !!(
		event &&
		(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey)
	);
}

function preventUnmodifiedEmployeeLinkNavigation(event) {
	const target = event.target;
	if (!target || typeof target.closest !== "function") {
		return;
	}
	if (!target.closest(`a[${EMPLOYEE_LINK_ATTR}]`)) {
		return;
	}
	if (isModifiedClick(event)) {
		return;
	}
	event.preventDefault();
}

if (typeof window !== "undefined" && !window.__plainEmployeeLinkClickGuard) {
	window.__plainEmployeeLinkClickGuard = true;
	window.addEventListener(
		"click",
		preventUnmodifiedEmployeeLinkNavigation,
		true
	);
}

/**
 * Componente enlace reutilizable. Por defecto renderiza un <Link> de react-router
 * a `/employees/:id`. Al hacer click abre el <EmployeeEditDrawer/> montado
 * localmente (sin pasar por showModal, para soportar apilado).
 *
 * - Cmd/Ctrl/Shift/Alt+Click → comportamiento por defecto del link
 *   (abrir página completa en nueva pestaña).
 * - `as="button"` renderiza un <button> en lugar del <Link>.
 * - `stopPropagation` (default true) evita que el click burbujee a manejadores
 *   superiores (útil cuando el padre ya tiene un onClick en la fila/celda).
 *
 * Props principales:
 *  - employee?: object
 *  - employeeId?: number | string
 *  - as?: "link" | "button" (default "link")
 *  - to?: string — override de la URL del <Link> (default /employees/:id)
 *  - onDrawerClose?: (hasDataChanges?: boolean) => void — callback al cerrar el drawer
 *  - className, style, children, title, ...
 */
export class EmployeeLink extends React.Component {
	state = { isOpen: false };

	handleClick = (e) => {
		const { as = "link", onClick, stopPropagation = true } = this.props;

		if (as === "link" && isModifiedClick(e)) {
			if (onClick) onClick(e);
			return;
		}

		if (e && e.preventDefault) e.preventDefault();
		if (stopPropagation && e && e.stopPropagation) e.stopPropagation();
		if (onClick) onClick(e);
		this.setState({ isOpen: true });
	};

	handleClickCapture = (e) => {
		if (this.props.as === "button") return;
		if (isModifiedClick(e)) return;
		if (e && e.preventDefault) e.preventDefault();
	};

	handleDrawerClose = (hasDataChanges = false) => {
		this.setState({ isOpen: false }, () => {
			if (typeof this.props.onDrawerClose === "function") {
				this.props.onDrawerClose(hasDataChanges);
			}
		});
	};

	render() {
		const {
			employee,
			employeeId,
			as = "link",
			to,
			className,
			style,
			title,
			children,
			initialTab,
			width,
			// excluded from ...rest
			// eslint-disable-next-line no-unused-vars
			onClick,
			// eslint-disable-next-line no-unused-vars
			stopPropagation,
			// eslint-disable-next-line no-unused-vars
			onDrawerClose,
			...rest
		} = this.props;

		const id = employeeId || (employee && employee.id);
		if (!id) return children || null;

		const linkTo = to || `/employees/${id}`;

		return (
			<>
				{as === "button" ? (
					<button
						type="button"
						className={className}
						style={style}
						title={title}
						onClick={this.handleClick}
						{...rest}
					>
						{children}
					</button>
				) : (
					<Link
						to={linkTo}
						className={className}
						style={style}
						title={title}
						{...rest}
						onClick={this.handleClick}
						onClickCapture={this.handleClickCapture}
						data-employee-link=""
					>
						{children}
					</Link>
				)}
				<EmployeeEditDrawer
					isOpen={this.state.isOpen}
					onClose={this.handleDrawerClose}
					employee={employee}
					employeeId={id}
					initialTab={initialTab}
					width={width}
				/>
			</>
		);
	}
}
